import { TableToolbar, TableToolbarContent, TableToolbarSearch } from "@carbon/react";
import { ChangeEvent, FC, useMemo, useState } from "react";

import { RelatedCasesTable } from "src/components/case/RelatedCasesTable";
import { useDimensions } from "src/components/DimensionedLayout/useDimensions";
import { PassiveModal } from "src/components/Modal";
import { useGetRuleExecutionResultAssociatedCases } from "src/graphql/anti-fraud/queries/rules/GetRuleExecutionResultAssociatedCasesQuery";
import { debounceSearchTime } from "src/hooks/useSearch";
import { useT } from "src/internationalization";
import { Case } from "src/types/case";
import { debounce } from "src/utils/functions.utils";

export type AssociatedCasesModalProps = {
  onClose: () => void;
  ruleExecutionId: string;
  caseTypeCode?: string;
};

export const AssociatedCasesModal: FC<AssociatedCasesModalProps> = ({
  onClose,
  ruleExecutionId,
  caseTypeCode,
}) => {
  const t = useT();
  const { pageSize } = useDimensions();
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const { data, loading, error, refetch } = useGetRuleExecutionResultAssociatedCases({
    ruleExecutionResultId: ruleExecutionId,
    offset: 0,
    limit: pageSize,
    searchString: searchTerm,
  });

  const cases = data?.cases ?? [];
  const totalCount = data?.pageInfo?.totalCount ?? 0;
  const pageOffset = data?.pageInfo?.offset ?? 0;

  const handleLoadMore = (startIndex: number, stopIndex: number) => {
    if (!loading) {
      refetch({
        offset: startIndex,
        limit: stopIndex - startIndex + 1,
      });
    }
  };

  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setSearchTerm(value), debounceSearchTime),
    []
  );

  const handleSearchChange = (event: "" | ChangeEvent<HTMLInputElement>) => {
    if (!event) return;
    const value = event.target.value;
    setSearchInput(value);
    debouncedSetSearchTerm(value);
  };

  const handleSearchClear = () => {
    setSearchInput("");
    setSearchTerm("");
  };

  const toolbar = () => (
    <TableToolbar aria-label="associated cases toolbar">
      <TableToolbarContent>
        <TableToolbarSearch
          placeholder={t("page.cases.tab.requests.table.search.placeholder")}
          onChange={handleSearchChange}
          onClear={handleSearchClear}
          persistent={true}
          value={searchInput}
        />
      </TableToolbarContent>
    </TableToolbar>
  );

  return (
    <PassiveModal
      onClose={onClose}
      size="xl"
      title={t("cases.relatedCases.modal.title", {
        number: totalCount,
      })}
      fullHeight
    >
      <div className="full-height">
        <RelatedCasesTable
          cases={cases as Case[]}
          loading={loading}
          isError={!!error}
          totalCount={totalCount}
          fetchMore={handleLoadMore}
          pageOffset={pageOffset}
          caseTypeCode={caseTypeCode}
          toolbar={toolbar}
        />
      </div>
    </PassiveModal>
  );
};
